import { IndexScene, ModelScene, Period } from 'components';
import { CompanyFilterChip, CompanyTitle } from 'modules/core/company/components';
import { useAgencyTypeOptions } from 'modules/core/useEnabledTypeOptions';
import { SelectFilterChip } from 'components/filters/SelectFilterChip';
import { Box } from '@ff-it/ui';
import { agencyScopeRequired, never } from 'core/permissions';
import { OutletOrServiceCategoryFilterChip } from 'modules/supplier/outletOrServiceCategory/OutletOrServiceCategoryFilterChip';
import type { EmbeddedEnterprise } from 'modules/core/types';
import type { EmbeddedCompany } from 'types';
import { Link } from 'react-router-dom';
import type { Vendor } from 'modules/supplier/vendor/types';
import type { EmbeddedOutletOrServiceCategory } from 'modules/supplier/outletOrServiceCategory/types';
import { OutletOrServiceCategoryTitle } from 'modules/supplier/outletOrServiceCategory/OutletOrServiceCategoryTitle';
import type { EmbeddedProduct } from 'modules/supplier/products';
import { TitleContainer } from 'components/TitleContainer';
import { VendorFilterChip } from 'modules/supplier/vendor/VendorFilterChip';
import { ListExport } from 'components/ListActions';
import { ControlledTableColumns, flexColumn } from 'components/ControlledTable';
import { ReactElement } from 'react';

type ProvidedRow = {
  id: number;
  rel: string;
  is_direct: boolean;
  agency: EmbeddedEnterprise;
  provider: EmbeddedCompany;
  agreement: {
    id: number;
    number: string;
    date_from: string;
    date_to: string | null;
  };
  vendor: Vendor | null;
  outlet_or_service_category: EmbeddedOutletOrServiceCategory | null;
  product: Pick<EmbeddedProduct, 'id' | 'name' | 'kind' | 'type' | 'disabled'> | null;
};

const columns: ControlledTableColumns<ProvidedRow> = [
  {
    key: 'agency.id',
    content: 'Proxy',
    cellProps: { className: 'py-0' },
    render: ({ agency, is_direct }) =>
      is_direct ? <small className="text-muted">Direct</small> : <CompanyTitle {...agency.company} />,
  },
  flexColumn({
    key: 'provider.id',
    content: 'Provider',
    render: ({ provider }) => <CompanyTitle {...provider} />,
    cellProps: { className: 'py-0' },
  }),
  {
    key: 'agreement.id',
    content: 'Agreement',
    render: ({ agency, provider, agreement, rel }) => (
      <Box display="flex" alignItems="center">
        <TitleContainer
          title={
            <Link
              to={`/${agency.slug}/contacts/providers/${provider.parent || provider.id}/agreements/${agreement.id}`}
            >
              {agreement.number}
            </Link>
          }
          subtitle={rel}
        />

        <Box ml="auto" pl="2">
          <Period date_from={agreement.date_from} date_to={agreement.date_to} />
        </Box>
      </Box>
    ),
    cellProps: { className: 'py-0' },
  },
  {
    key: 'vendor.id',
    content: 'Vendor',
    render: ({ vendor }) => vendor?.name,
  },
  {
    key: 'outlet_or_service_category.id',
    content: 'Outlet or service category',
    render: ({ outlet_or_service_category }) =>
      outlet_or_service_category ? <OutletOrServiceCategoryTitle {...outlet_or_service_category} /> : null,
    cellProps: { className: 'py-0' },
  },

  {
    key: 'product.id',
    content: 'Product',
    render: ({ product }) =>
      product ? <TitleContainer title={product.name} disabled={product.disabled} subtitle={product.kind} /> : null,
    cellProps: { className: 'py-0' },
  },
  {
    key: 'product.type',
    content: 'Type',
    render: ({ product }) => (product ? product.type : null),
    width: '100px',
    cellProps: {
      textAlign: 'right',
    },
  },
];

const directOptions = [
  {
    value: 'true',
    label: 'Direct',
  },
  {
    value: 'false',
    label: 'Proxy',
  },
];

function Provided(): ReactElement {
  const typeOptions = useAgencyTypeOptions();
  return (
    <ModelScene title="Provided" entityTitle={() => 'Pluraltitle'} pluralTitle="Provided" endpoint="supplier/provided">
      <IndexScene
        perm={agencyScopeRequired}
        canCreate={never}
        columns={columns}
        toolbar={<ListExport />}
        filterFields={
          <>
            <CompanyFilterChip name="provider" />
            <VendorFilterChip name="vendor" />
            <OutletOrServiceCategoryFilterChip name="outlet_or_service_category" label="Outlet / service category" />
            <SelectFilterChip name="type" options={typeOptions} />
            <SelectFilterChip name="direct" label="Direct or proxy" options={directOptions} />
          </>
        }
      />
    </ModelScene>
  );
}

export const route = {
  path: 'provided',
  element: <Provided />,
};
